/**
 * Links findings to the session steps they were reported against, so the transcript
 * timeline can pin each finding to the screen that provoked it.
 *
 * The agent cites a step by the `step` number the runner hands back with every browser
 * action (agentLoop.ts dispatchTool), which is the transcript step's own idx — so
 * `stepIndex:N` and `screenshot:N` both mean step N.
 *
 * Sessions recorded before that grounding landed are best-effort only: the agent was
 * shown no numbering and invented refs ('step_7', '6_readScreen', 'readScreen_response',
 * once a URL), and cross-checking those against the steps they name shows they often
 * disagree. We parse a number out where there is one and keep it only if it's a real
 * step; a pin on such a session may still be wrong. Those findings age out with the
 * container's 30-day TTL. Anything unresolved is simply left off the timeline — the
 * Findings list above the transcript always shows every finding regardless.
 */
import type { AgentFeedback, AgentSession, AgentSessionStep } from '../../api/adminApi';

function resolveArtifact(
  artifact: { kind: string; ref: string },
  steps: AgentSessionStep[],
  byIdx: Map<number, AgentSessionStep>,
): number | undefined {
  if (artifact.kind !== 'screenshot' && artifact.kind !== 'stepIndex') return undefined;
  const ref = artifact.ref.trim();

  // A ref naming a capture outright (a blob name) is checked first: its own path carries
  // digits that the number parse below would happily mistake for a step.
  const byShot = steps.find(s => s.screenshotRef && ref.includes('/') && s.screenshotRef.includes(ref));
  if (byShot) return byShot.idx;

  // The grounded form is a bare number; legacy refs bury it ('step_7', '6_readScreen'),
  // so take the last run of digits. Keep it only if it names a step that exists — that
  // check is what rejects noise like 'readScreen_response' or a pasted URL.
  const n = Number(ref.match(/(\d+)(?!.*\d)/s)?.[1]);
  return Number.isInteger(n) && byIdx.has(n) ? n : undefined;
}

/** step idx → findings citing that step. A finding citing several steps appears on each. */
export function findingsByStep(findings: AgentFeedback[], steps: AgentSessionStep[]): Map<number, AgentFeedback[]> {
  const byIdx = new Map(steps.map(s => [s.idx, s]));
  const out = new Map<number, AgentFeedback[]>();
  for (const f of findings) {
    const idxs = new Set<number>();
    for (const a of f.artifacts ?? []) {
      const idx = resolveArtifact(a, steps, byIdx);
      if (idx !== undefined) idxs.add(idx);
    }
    for (const idx of idxs) out.set(idx, [...(out.get(idx) ?? []), f]);
  }
  return out;
}

/** The findings belonging to one session. The session's own feedbackIds are the
 *  authoritative link (written at finalize); older sessions predate it, so fall back to
 *  matching on sessionId, then persona. */
export function sessionFindings(session: AgentSession, feedback: AgentFeedback[]): AgentFeedback[] {
  const ids = new Set(session.feedbackIds ?? []);
  if (ids.size) return feedback.filter(f => ids.has(f.id));
  return feedback.filter(f => (f.sessionId ? f.sessionId === session.id : f.personaId === session.personaId));
}
